import React, { useState, useEffect, useMemo } from "react";
import ButtonCTA from "./ButtonCTA";
import RatingDisplay from "./RatingDisplay";

const HeroSection = () => {
  const licenseTypes = [
    "Security Guard License",
    "Uber license",
    "Private Investigator License",
    "Real Estate Agent License",
    "Tow Operator License",
  ];

  const [currentLicenseIndex, setCurrentLicenseIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isAnimationEnabled, setIsAnimationEnabled] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Calculate the width needed for the longest license type
  const containerWidth = useMemo(() => {
    // Find the longest string in the licenseTypes array
    const longestString = licenseTypes.reduce(
      (longest, current) =>
        current.length > longest.length ? current : longest,
      "",
    );
    // Approximate width calculation: each character is roughly 10px wide in this font size
    // Adding extra padding (40px) for safety and visual spacing
    return `${longestString.length * 10 + 40}px`;
  }, [licenseTypes]);

  // Check if screen width is less than 400px
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 400);
    };

    // Initial check
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Check if user prefers reduced motion
  useEffect(() => {
    // Create media query list for prefers-reduced-motion
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Define handler for changes to the preference
    const handleMediaChange = (event: {
      matches: boolean | ((prevState: boolean) => boolean);
    }) => {
      setPrefersReducedMotion(event.matches);
    };

    // Add event listener for changes
    mediaQuery.addEventListener("change", handleMediaChange);

    // Cleanup
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  // Check if page is fully loaded
  useEffect(() => {
    // If document is already loaded, set isPageLoaded to true
    if (document.readyState === "complete") {
      setIsPageLoaded(true);
    } else {
      // Otherwise, listen for the load event
      const handleLoad = () => {
        setIsPageLoaded(true);
      };

      window.addEventListener("load", handleLoad);

      // Cleanup
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  // Enable animation after page is loaded
  useEffect(() => {
    if (isPageLoaded) {
      // Small delay to ensure a smooth transition
      const timeoutId = setTimeout(() => {
        setIsAnimationEnabled(true);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isPageLoaded]);

  // Animation logic - optimized to reduce jank and improve performance
  useEffect(() => {
    // Don't start animation until page is fully loaded and animation is enabled
    if (!isPageLoaded || !isAnimationEnabled) return;

    if (prefersReducedMotion) {
      // Simple rotation for reduced motion preference
      const interval = setInterval(() => {
        setCurrentLicenseIndex((prev) => (prev + 1) % licenseTypes.length);
      }, 2000); // Change every 2 seconds if motion is reduced
      return () => clearInterval(interval);
    }

    // Use requestAnimationFrame for smoother animations
    let rafId: number;
    let animationTimeoutId: NodeJS.Timeout;
    let pauseTimeoutId: NodeJS.Timeout;
    let isRunning = true;

    const animationCycle = () => {
      if (!isRunning) return;

      // Pause for 2 seconds before starting the animation
      pauseTimeoutId = setTimeout(() => {
        if (!isRunning) return;

        // Use requestAnimationFrame to sync with browser's refresh rate
        rafId = requestAnimationFrame(() => {
          setIsAnimating(true);
        });

        // Animation duration (700ms)
        animationTimeoutId = setTimeout(() => {
          if (!isRunning) return;

          setCurrentLicenseIndex((prev) => (prev + 1) % licenseTypes.length);
          setIsAnimating(false);

          // Schedule next cycle
          animationCycle();
        }, 700);
      }, 2000);
    };

    // Start the first cycle
    animationCycle();

    // Cleanup function
    return () => {
      isRunning = false;
      clearTimeout(pauseTimeoutId);
      clearTimeout(animationTimeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    licenseTypes.length,
    prefersReducedMotion,
    isPageLoaded,
    isAnimationEnabled,
  ]);

  // Get the next license index
  const nextLicenseIndex = (currentLicenseIndex + 1) % licenseTypes.length;

  return (
    <section className="bg-emerald-700 text-white py-8 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
          Criminal Record & Judicial Matters Check (CRJMC)
        </h1>
        <h2 className="text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-6">
          Get Your Criminal Record & Judicial Matters Check (CRJMC) in only 15
          minutes online.
        </h2>
        <div className="grid md:grid-cols-2 gap-6 sm:gap-6 md:gap-8">
          <div>
            <div className="text-lg sm:text-xl lg:text-2xl mb-6">
              Complete a brief online questionnaire and receive your police
              CRJMC check by email. Fast, affordable, and officially accepted
              for{" "}
              <span
                className={`inline-block bg-emerald-900/95 px-2 rounded text-center align-middle overflow-hidden relative flex items-center justify-center ${
                  isSmallScreen ? "w-full block" : "whitespace-nowrap"
                }`}
                style={{
                  ...(isSmallScreen
                    ? {}
                    : {
                        minWidth: containerWidth,
                        width: containerWidth,
                      }),
                  height: "1.5em", // Fixed height to prevent layout shifts
                }}
              >
                <div
                  className="relative h-full w-full overflow-hidden"
                  style={{
                    contentVisibility: "auto",
                    contain: "paint layout",
                    isolation: "isolate",
                    // Removed perspective which can cause issues with text rendering on iOS
                  }}
                >
                  {/* Static display when animation is not enabled */}
                  {!isAnimationEnabled && (
                    <span className="absolute w-full text-center inset-0 flex items-center justify-center">
                      {licenseTypes[0]}{" "}
                      {/* Always show "Security Guard License" */}
                    </span>
                  )}

                  {/* Animated display when animation is enabled */}
                  {isAnimationEnabled && (
                    <>
                      {/* Current text (animates down and out) */}
                      <span
                        className={`absolute w-full text-center inset-0 flex items-center justify-center ${
                          prefersReducedMotion
                            ? ""
                            : isAnimating
                              ? "transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                              : ""
                        }`}
                        style={{
                          transform:
                            !prefersReducedMotion && isAnimating
                              ? "translate3d(0, 100%, 0)" // Use translate3d for GPU acceleration
                              : "translate3d(0, 0, 0)",
                          WebkitTransform:
                            !prefersReducedMotion && isAnimating
                              ? "translate3d(0, 100%, 0)" // Explicit -webkit prefix for iOS Safari
                              : "translate3d(0, 0, 0)",
                          opacity: !prefersReducedMotion && isAnimating ? 0 : 1,
                          transitionProperty:
                            "transform, -webkit-transform, opacity", // Include webkit prefix for iOS
                          transitionDuration:
                            !prefersReducedMotion && isAnimating
                              ? "700ms"
                              : "0ms", // No transition on reset
                          willChange: "transform, -webkit-transform, opacity", // Hint to browser which properties will change
                          // Removed transformStyle and backfaceVisibility which can cause issues on iOS
                          // Force hardware acceleration on iOS to prevent text disappearing
                          WebkitBackfaceVisibility: "visible",
                          WebkitTransformStyle: "flat",
                        }}
                      >
                        {licenseTypes[currentLicenseIndex]}
                      </span>

                      {/* Next text (comes from top) */}
                      {!prefersReducedMotion && (
                        <span
                          className={`absolute w-full text-center inset-0 flex items-center justify-center ${isAnimating ? "transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)]" : ""}`}
                          style={{
                            transform: isAnimating
                              ? "translate3d(0, 0, 0)" // Use translate3d for GPU acceleration
                              : "translate3d(0, -100%, 0)", // Start from top
                            WebkitTransform: isAnimating
                              ? "translate3d(0, 0, 0)" // Explicit -webkit prefix for iOS Safari
                              : "translate3d(0, -100%, 0)", // Start from top
                            opacity: isAnimating ? 1 : 0,
                            transitionProperty:
                              "transform, -webkit-transform, opacity", // Include webkit prefix for iOS
                            transitionDuration: isAnimating ? "700ms" : "0ms", // No transition on reset
                            willChange: "transform, -webkit-transform, opacity", // Hint to browser which properties will change
                            // Removed transformStyle and backfaceVisibility which can cause issues on iOS
                            // Force hardware acceleration on iOS to prevent text disappearing
                            WebkitBackfaceVisibility: "visible",
                            WebkitTransformStyle: "flat",
                          }}
                        >
                          {licenseTypes[nextLicenseIndex]}
                        </span>
                      )}
                      {/* Simple display for reduced motion */}
                      {prefersReducedMotion && (
                        <span className="absolute w-full text-center inset-0 flex items-center justify-center">
                          {licenseTypes[currentLicenseIndex]}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </span>{" "}
              in Ontario.
            </div>
            <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg lg:text-xl">
              <li className="flex items-center">
                <span className="mr-2">✓</span>
                Fully online, no need to visit a police station
              </li>
              <li className="flex items-center">
                <span className="mr-2">✓</span>
                Delivery within 15 minutes after filling out
              </li>
              <li className="flex items-center">
                <span className="mr-2">✓</span>
                Most affordable option available
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>
                  <span className="inline -mx-2 bg-yellow-100/80 text-emerald-900 px-2 py-1 rounded me-1">
                    Officially accepted
                  </span>
                </span>
              </li>
            </ul>
            <div className="mt-8 space-y-4 max-w-md mx-auto md:mx-0">
              <ButtonCTA />
              <div className="flex justify-center align-items-center rounded-lg p-4">
                <RatingDisplay variant="dark" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-start flex-col w-full">
            <div className="relative w-full">
              <div className="aspect-[4/3] relative overflow-hidden rounded-xl">
                {/* Add a placeholder div with the same dimensions as the image */}
                {!isImageLoaded && (
                  <div
                    className="absolute inset-0 rounded-xl"
                    aria-hidden="true"
                    style={{ aspectRatio: "4/3" }}
                  ></div>
                )}
                <picture>
                  <source
                    srcSet="/images/private-investigator/cleared-background-check-private-investigator-512.avif"
                    type="image/avif"
                  />
                  <source
                    srcSet="/images/private-investigator/cleared-background-check-private-investigator-512.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/private-investigator/cleared-background-check-private-investigator-512.png"
                    alt="Cleared Background Check for Private Investigator"
                    className="w-full h-full object-cover rounded-xl shadow-lg"
                    width={512}
                    height={512}
                    loading="eager"
                    decoding="async"
                    onLoad={() => {
                      // Set image as loaded to hide the placeholder
                      setIsImageLoaded(true);
                    }}
                  />
                </picture>
                <div className="absolute bottom-0 left-0 right-0 bg-emerald-900/95 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-emerald-200 text-4xl leading-none font-serif">
                      "
                    </span>
                    <div>
                      <p className="text-white text-sm sm:text-base">
                        I received my background check report via email within
                        13 minutes. CRJMC.net is amazing!
                      </p>
                      <p className="text-emerald-200 text-sm mt-1">
                        Michael R. - Security Guard License Applicant
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 mb-8">
              <span className="inline -mx-2 bg-yellow-100/80 text-emerald-900 px-3 py-2 rounded text-lg">
                Issued by
              </span>
              <span className="inline sm:ml-2 -mx-2 bg-yellow-100/80 text-emerald-900 px-3 py-2 rounded text-lg">
                an Authorized Third Party Provider
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
